import React, { useState, useEffect } from 'react';
import { uploadData, getUrl, remove, list } from 'aws-amplify/storage';
import { getCurrentUser } from 'aws-amplify/auth';
import './Lyrics.css';

const Lyrics = () => {
  const [lyrics, setLyrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewLyricForm, setShowNewLyricForm] = useState(false);
  const [newLyricTitle, setNewLyricTitle] = useState('');
  const [newLyricContent, setNewLyricContent] = useState('');
  const [editingLyric, setEditingLyric] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [username, setUsername] = useState('');
  const [expandedLyrics, setExpandedLyrics] = useState({});

  useEffect(() => {
    fetchLyrics();
    fetchUsername();
  }, []);

  const fetchUsername = async () => {
    try {
      const user = await getCurrentUser();
      setUsername(user.username);
    } catch (error) {
      console.error('Error fetching username:', error);
    }
  };

  const fetchLyrics = async () => {
    try {
      setLoading(true);
      const result = await list({
        prefix: 'lyrics/'
      });

      // Process the results to get only lyric files
      const lyricFiles = result.items.filter(item => 
        item.key.startsWith('lyrics/') && 
        item.key !== 'lyrics/' && 
        !item.key.endsWith('/')
      );

      // Fetch content for each lyric file
      const lyricsWithContent = await Promise.all(
        lyricFiles.map(async (file) => {
          try {
            const urlResult = await getUrl({
              key: file.key
            });
            
            const response = await fetch(urlResult.url);
            const content = await response.text();
            
            // Extract metadata from the filename
            const filename = file.key.split('/').pop();
            const nameWithoutExtension = filename.replace('.txt', '');
            const parts = nameWithoutExtension.split('__');
            
            let title = nameWithoutExtension;
            let author = 'Unknown';
            let date = new Date().toISOString();
            
            if (parts.length >= 3) {
              title = parts[0].replace(/_/g, ' ');
              author = parts[1].replace(/_/g, ' ');
              date = new Date(parseInt(parts[2])).toISOString();
            }
            
            return {
              key: file.key,
              title,
              author,
              date,
              content,
              lastModified: file.lastModified || new Date().toISOString()
            };
          } catch (error) {
            console.error(`Error fetching content for ${file.key}:`, error);
            return {
              key: file.key,
              title: file.key.split('/').pop().replace('.txt', '').replace(/_/g, ' '),
              author: 'Unknown',
              content: 'Error loading content',
              lastModified: file.lastModified || new Date().toISOString()
            };
          }
        })
      );

      // Sort by last modified date (newest first)
      lyricsWithContent.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
      
      setLyrics(lyricsWithContent);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching lyrics:', error);
      setError('Failed to load lyrics. Please try again.');
      setLoading(false);
    }
  };

  const handleCreateLyric = async (e) => {
    e.preventDefault();
    
    if (!newLyricTitle.trim()) {
      setError('Please enter a title for your lyrics.');
      return;
    }
    
    try {
      const timestamp = Date.now();
      const formattedTitle = newLyricTitle.trim().replace(/\s+/g, '_');
      const formattedUsername = username.replace(/\s+/g, '_');
      
      // Format: Title__Author__Timestamp.txt
      const key = `lyrics/${formattedTitle}__${formattedUsername}__${timestamp}.txt`;
      
      await uploadData({
        key,
        data: newLyricContent,
        options: {
          contentType: 'text/plain'
        }
      }).result;
      
      // Reset form
      setNewLyricTitle('');
      setNewLyricContent('');
      setShowNewLyricForm(false);
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error creating lyric:', error);
      setError('Failed to create lyric. Please try again.');
    }
  };

  const handleUpdateLyric = async () => {
    if (!editingLyric) return;
    
    try {
      await uploadData({
        key: editingLyric.key,
        data: editContent,
        options: {
          contentType: 'text/plain'
        }
      }).result;
      
      // Reset editing state
      setEditingLyric(null);
      setEditContent('');
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error updating lyric:', error);
      setError('Failed to update lyric. Please try again.');
    }
  };

  const handleDeleteLyric = async (key) => {
    if (!window.confirm('Are you sure you want to delete these lyrics? This action cannot be undone.')) {
      return;
    }
    
    try {
      await remove({ key });
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error deleting lyric:', error);
      setError('Failed to delete lyric. Please try again.');
    }
  };

  const startEditing = (lyric) => {
    setEditingLyric(lyric);
    setEditContent(lyric.content);
  };

  const cancelEditing = () => {
    setEditingLyric(null);
    setEditContent('');
  };

  const toggleLyricExpansion = (key) => {
    setExpandedLyrics(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="lyrics-container">
      <h2>Band Lyrics</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button className="dismiss-button" onClick={() => setError('')}>×</button>
        </div>
      )}
      
      <div className="lyrics-actions">
        <button 
          className="new-lyric-button"
          onClick={() => setShowNewLyricForm(!showNewLyricForm)}
        >
          {showNewLyricForm ? 'Cancel' : 'Add New Lyrics'}
        </button>
      </div>
      
      {showNewLyricForm && (
        <div className="lyric-form">
          <h3>Create New Lyrics</h3>
          <form onSubmit={handleCreateLyric}>
            <div className="form-group">
              <label htmlFor="lyric-title">Song Title</label>
              <input
                id="lyric-title"
                type="text"
                value={newLyricTitle}
                onChange={(e) => setNewLyricTitle(e.target.value)}
                placeholder="Enter song title"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lyric-content">Lyrics</label>
              <textarea
                id="lyric-content"
                value={newLyricContent}
                onChange={(e) => setNewLyricContent(e.target.value)}
                placeholder="Enter lyrics here..."
                rows={10}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="save-button">Save Lyrics</button>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setShowNewLyricForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {loading ? (
        <div className="loading-lyrics">Loading lyrics...</div>
      ) : lyrics.length === 0 ? (
        <div className="no-lyrics">
          <p>No lyrics have been added yet.</p>
          <p>Click "Add New Lyrics" to create the first song lyrics.</p>
        </div>
      ) : (
        <div className="lyrics-list">
          {lyrics.map((lyric) => (
            <div key={lyric.key} className="lyric-card">
              {editingLyric && editingLyric.key === lyric.key ? (
                <div className="lyric-edit">
                  <h3>{lyric.title}</h3>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={15}
                  />
                  <div className="edit-actions">
                    <button onClick={handleUpdateLyric} className="save-button">Save Changes</button>
                    <button onClick={cancelEditing} className="cancel-button">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="lyric-header" onClick={() => toggleLyricExpansion(lyric.key)}>
                    <div className="lyric-title-row">
                      <h3>{lyric.title}</h3>
                      <span className="expand-icon">
                        {expandedLyrics[lyric.key] ? '▼' : '►'}
                      </span>
                    </div>
                    <div className="lyric-meta">
                      <span className="lyric-author">By: {lyric.author}</span>
                      <span className="lyric-date">Last updated: {formatDate(lyric.lastModified)}</span>
                    </div>
                  </div>
                  
                  {expandedLyrics[lyric.key] && (
                    <>
                      <div className="lyric-content">
                        <pre>{lyric.content}</pre>
                      </div>
                      <div className="lyric-actions">
                        <button onClick={() => startEditing(lyric)} className="edit-button">Edit</button>
                        <button onClick={() => handleDeleteLyric(lyric.key)} className="delete-button">Delete</button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      
      <div className="band-logo">
        <img src="/lm.png" alt="Band Logo" style={{ maxWidth: '300px', marginTop: '40px' }} />
      </div>
    </div>
  );
};

export default Lyrics;
